// Product HTML for the quick view. Once the product section's id is known (learned from the first full page,
// then remembered per theme), only that section is fetched via the Section Rendering API: ~120 KB instead of
// the ~850 KB full page, and much faster to render. Falls back to the full page if that fails. Requests are
// cached for a few minutes and start on hover / touch, so opening the quick view rarely waits on the network.
window.quickAddHTML =
  window.quickAddHTML ||
  (() => {
    const KEY = 'quick-add-section:' + ((window.Shopify && Shopify.theme && Shopify.theme.id) || '');
    const MAX_AGE = 5 * 60 * 1000;
    const cache = new Map();
    let sectionId = null;
    try {
      sectionId = localStorage.getItem(KEY);
    } catch (e) {}

    const learn = (html) => {
      const match = html.match(/<product-info[^>]*\sdata-section="([^"]+)"/);
      if (!match || match[1] === sectionId) return;
      sectionId = match[1];
      try {
        localStorage.setItem(KEY, sectionId);
      } catch (e) {}
    };
    const text = (response) => {
      if (!response.ok) throw new Error(response.status);
      return response.text();
    };
    const fullPage = (url) =>
      fetch(url)
        .then(text)
        .then((html) => {
          learn(html);
          return html;
        });
    const sectionOnly = (url) =>
      fetch(url + (url.includes('?') ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId))
        .then(text)
        .then((html) => {
          if (!html.includes('<product-info')) throw new Error('no product-info');
          return html;
        });

    function load(url) {
      const cached = cache.get(url);
      if (cached && Date.now() - cached.at < MAX_AGE) return cached.request;
      const request = (sectionId ? sectionOnly(url) : Promise.reject()).catch(() => fullPage(url));
      cache.set(url, { at: Date.now(), request: request });
      request.catch(() => cache.delete(url));
      return request;
    }

    // Warm the cache when a product card is about to be opened
    const urlFor = (target) => {
      const card = target.closest && target.closest('.card-wrapper, .product-card-wrapper, .grid__item');
      const button = card && card.querySelector('modal-opener [data-product-url]');
      return button ? button.getAttribute('data-product-url') : null;
    };
    let hoverTimer;
    document.addEventListener(
      'pointerover',
      (event) => {
        if (event.pointerType !== 'mouse') return;
        const url = urlFor(event.target);
        clearTimeout(hoverTimer);
        if (url) hoverTimer = setTimeout(() => load(url).catch(() => {}), 80);
      },
      { passive: true }
    );
    document.addEventListener(
      'pointerdown',
      (event) => {
        const url = urlFor(event.target);
        if (url) load(url).catch(() => {});
      },
      { passive: true }
    );

    return { load: load };
  })();

if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        window.quickAddHTML
          .load(opener.getAttribute('data-product-url'))
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.skipScriptsAlreadyRun(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      // Data and component-definition scripts only need to run once per page. Re-running them on every quick view
      // (find-my-size-data.js alone is 80 KB) kept the browser busy for ~0.35s after the popup opened.
      // Scripts that set up the new markup (find-my-size.js, app scripts) still run each time.
      skipScriptsAlreadyRun(productElement) {
        const ONCE = /\/(find-my-size-data|product-info|product-form|pickup-availability|product-modal|media-gallery)\.js$/;
        const ran = (QuickAddModal.scriptsRun = QuickAddModal.scriptsRun || new Set());
        const pathOf = (src) => {
          try {
            return new URL(src, window.location.href).pathname;
          } catch (e) {
            return src;
          }
        };
        Array.from(document.scripts).forEach((script) => script.src && ran.add(pathOf(script.src)));
        productElement.querySelectorAll('script[src]').forEach((script) => {
          const path = pathOf(script.getAttribute('src'));
          if (!ONCE.test(path)) return;
          if (ran.has(path)) script.remove();
          else ran.add(path);
        });
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
